import { Input } from '@remarkable/ark-web';
import type { HTMLProps } from 'react';
import { cn } from 'src/utils/classNamesHelper';
import { Button } from './Button';

type NumberInputProps = {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  name?: string;
  hasError?: boolean;
};

export function NumberInput({
  value,
  onChange,
  min = 1,
  max,
  name = 'number',
  className,
  id,
  hasError = true,
  ref,
  ...props
}: HTMLProps<HTMLInputElement> & NumberInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      onChange(null);
      return;
    }
    const newValue = parseInt(e.target.value, 10);
    if (isNaN(newValue)) {
      onChange(null);
    } else {
      onChange(newValue);
    }
  };

  const currentValue = value !== null ? value : 0;

  return (
    <div className={cn('flex items-center gap-8', className)} {...props}>
      <Button
        variant="secondary"
        onClick={() => onChange(Math.max(currentValue - 1, min))}
        disabled={currentValue <= min}
        aria-label="Decrease value"
        type="button"
      >
        -
      </Button>
      <Input
        id={id}
        name={name}
        type="number"
        value={value ?? ''}
        onChange={handleChange}
        className="max-w-[80px] text-center"
        hasError={hasError}
        aria-label="Number input"
        ref={ref}
      />
      <Button
        variant="secondary"
        onClick={() =>
          onChange(
            max !== undefined
              ? Math.min(currentValue + 1, max)
              : currentValue + 1
          )
        }
        disabled={max !== undefined && currentValue >= max}
        aria-label="Increase value"
        type="button"
      >
        +
      </Button>
    </div>
  );
}
